// src/hooks/usePazYSalvoNotifications.js
import { useEffect, useCallback } from 'react';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import useNotifications from './useNotifications';

export const usePazYSalvoNotifications = () => {
    const {
        notifyPazYSalvoCreated,
        notifyPazYSalvoEnviadoAFirma,
        notifyPazYSalvoFirmado,
        notifyPazYSalvoAprobado,
        notifyPazYSalvoRechazado,
        notifyPazYSalvoCerrado,
        notifyPazYSalvoObservado,
        notifyPazYSalvoExcepcionCreada
    } = useNotificationContext();

    const { user } = useAuth();
    const { connection } = useNotifications();

    // Función para manejar notificaciones de Paz y Salvo
    const handlePazYSalvoNotification = useCallback((notification) => {
        switch (notification.type) {
            case 'PazYSalvoCreated':
                notifyPazYSalvoCreated(notification.data);
                break;
            case 'PazYSalvoEnviadoAFirma':
                notifyPazYSalvoEnviadoAFirma(notification.data);
                break;
            case 'PazYSalvoFirmado':
                notifyPazYSalvoFirmado(notification.data.pazYSalvo, notification.data.firmante);
                break;
            case 'PazYSalvoAprobado':
                notifyPazYSalvoAprobado(notification.data);
                break;
            case 'PazYSalvoRechazado':
                notifyPazYSalvoRechazado(notification.data.pazYSalvo, notification.data.motivo);
                break;
            case 'PazYSalvoCerrado':
                notifyPazYSalvoCerrado(notification.data);
                break;
            case 'PazYSalvoObservado':
                notifyPazYSalvoObservado(notification.data.pazYSalvo, notification.data.observacion);
                break;
            case 'PazYSalvoExcepcionCreada':
                notifyPazYSalvoExcepcionCreada(notification.data.pazYSalvo, notification.data.excepcion);
                break;
            default:
                console.warn('Unknown Paz y Salvo notification type:', notification.type);
        }
    }, [
        notifyPazYSalvoCreated,
        notifyPazYSalvoEnviadoAFirma,
        notifyPazYSalvoFirmado,
        notifyPazYSalvoAprobado,
        notifyPazYSalvoRechazado,
        notifyPazYSalvoCerrado,
        notifyPazYSalvoObservado,
        notifyPazYSalvoExcepcionCreada
    ]);

    // Suscribirse automáticamente a notificaciones de Paz y Salvo cuando la conexión esté disponible
    useEffect(() => {
        if (!connection || !user) return;

        // Guardar referencias de handlers para poder desregistrarlas exactamente
        const onCreated      = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoCreated', data });
        const onEnviado      = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoEnviadoAFirma', data });
        const onFirmado      = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoFirmado', data });
        const onAprobado     = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoAprobado', data });
        const onRechazado    = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoRechazado', data });
        const onCerrado      = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoCerrado', data });
        const onObservado    = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoObservado', data });
        const onExcepcion    = (data) => handlePazYSalvoNotification({ type: 'PazYSalvoExcepcionCreada', data });

        // Suscribirse a notificaciones generales de Paz y Salvo
        connection.on('PazYSalvoNotification', handlePazYSalvoNotification);

        // Suscribirse a notificaciones específicas por rol
        if (user.role === 'admin' || user.role === 'rrhh') {
            connection.on('PazYSalvoCreated', onCreated);
        }
        if (user.role === 'admin' || user.role === 'ti' || user.role === 'rrhh') {
            connection.on('PazYSalvoEnviadoAFirma', onEnviado);
            connection.on('PazYSalvoFirmado', onFirmado);
            connection.on('PazYSalvoObservado', onObservado);
        }
        if (user.role === 'admin' || user.role === 'rrhh') {
            connection.on('PazYSalvoAprobado', onAprobado);
            connection.on('PazYSalvoRechazado', onRechazado);
            connection.on('PazYSalvoCerrado', onCerrado);
            connection.on('PazYSalvoExcepcionCreada', onExcepcion);
        }

        return () => {
            // Limpiar exactamente los listeners registrados (con referencia de función)
            connection.off('PazYSalvoNotification', handlePazYSalvoNotification);
            connection.off('PazYSalvoCreated', onCreated);
            connection.off('PazYSalvoEnviadoAFirma', onEnviado);
            connection.off('PazYSalvoFirmado', onFirmado);
            connection.off('PazYSalvoAprobado', onAprobado);
            connection.off('PazYSalvoRechazado', onRechazado);
            connection.off('PazYSalvoCerrado', onCerrado);
            connection.off('PazYSalvoObservado', onObservado);
            connection.off('PazYSalvoExcepcionCreada', onExcepcion);
        };
    }, [connection, user, handlePazYSalvoNotification]);

    return {
        handlePazYSalvoNotification
    };
};
